/**
 * Phase 14 — Post-Payment landing + account activation
 * -----------------------------------------------------
 * Reads the signed post-payment token from the URL and calls
 *   GET /api/onboarding/from-payment/{token}
 * to render an account summary. If the user hasn't yet set an
 * app password (fresh PMPro checkout), we show a secure Password /
 * Confirm Password form on THIS page. Submission calls
 *   POST /api/onboarding/activate
 * which mints a real auth JWT, so the user is signed in and routed
 * straight into their role dashboard/onboarding — no forgot-password
 * detour required.
 */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Loader2, ArrowRight, AlertCircle, Lock, CheckCircle2, LogOut } from 'lucide-react';
import theme from '../theme';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Decode a JWT payload client-side without verifying the signature.
 * We only use this to compare the *claimed* email of an existing app
 * session against the email in the post-payment token, so that we can
 * surface a session-mismatch warning. Never use this for authorization.
 */
function decodeJwtEmail(jwt) {
  try {
    if (!jwt || typeof jwt !== 'string') return null;
    const parts = jwt.split('.');
    if (parts.length < 2) return null;
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const pad = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    const json = JSON.parse(atob(pad));
    return (json && typeof json.email === 'string') ? json.email.toLowerCase() : null;
  } catch (_) { return null; }
}

const Card = ({ children }) => (
  <div style={{
    maxWidth: 520, margin: '0 auto', marginTop: 96, padding: 32,
    background: theme.colors.bg.card, border: `1px solid ${theme.colors.borders}`,
    borderRadius: theme.radius.card, boxShadow: theme.colors.shadows.md,
  }}>{children}</div>
);

export default function PostPaymentPage({ setUser }) {
  const { token } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, ok: false, ctx: null, err: null });
  const [pw1, setPw1] = useState('');
  const [pw2, setPw2] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  // Session-isolation guard: read any pre-existing local session AT MOUNT time.
  const [existingSessionEmail] = useState(() =>
    decodeJwtEmail(localStorage.getItem('token'))
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/onboarding/from-payment/${token}`);
        if (cancelled) return;
        setState({ loading: false, ok: true, ctx: res.data, err: null });
      } catch (e) {
        if (cancelled) return;
        setState({
          loading: false, ok: false, ctx: null,
          err: e?.response?.data?.detail || 'Invalid or expired link',
        });
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const handleActivate = async (e) => {
    e && e.preventDefault && e.preventDefault();
    setFormError('');
    if (pw1.length < 8) { setFormError('Password must be at least 8 characters.'); return; }
    if (pw1 !== pw2)     { setFormError('Passwords do not match.'); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/onboarding/activate`, { token, password: pw1 });
      const { token: authToken, user, onboarding_route } = res.data || {};
      if (authToken) {
        localStorage.setItem('token', authToken);
        axios.defaults.headers.common['Authorization'] = `Bearer ${authToken}`;
      }
      if (user && typeof setUser === 'function') setUser(user);
      navigate(onboarding_route || '/', { replace: true });
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Activation failed. Please try again.';
      setFormError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (state.loading) {
    return (
      <Card>
        <div data-testid="post-payment-loading" style={{ display: 'flex', alignItems: 'center', gap: 10, color: theme.colors.text.muted }}>
          <Loader2 size={16} /> Loading your Kickback Contract setup…
        </div>
      </Card>
    );
  }
  if (!state.ok) {
    // Session-aware error state — fixes BUG #16. If a user is already
    // signed in on this browser, show their identity explicitly and
    // offer targeted actions instead of a silent "Login" link that
    // would land them in whatever dashboard the stored JWT points at.
    const handleSignOutErr = () => {
      try {
        localStorage.removeItem('token');
        delete axios.defaults.headers.common['Authorization'];
      } catch (_) { /* ignore */ }
      if (typeof setUser === 'function') setUser(null);
      navigate('/auth');
    };
    return (
      <Card>
        <div data-testid="post-payment-error" style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, color: theme.colors.status.error, fontWeight: 700 }}>
          <AlertCircle size={18} /> We couldn&apos;t verify your setup link
        </div>
        <div style={{ color: theme.colors.text.secondary, marginBottom: 16 }}>
          {state.err}
        </div>
        {existingSessionEmail && (
          <div
            data-testid="post-payment-error-session-context"
            style={{
              padding: 12, marginBottom: 16,
              background: '#f8fafc', border: `1px solid ${theme.colors.borders}`,
              borderRadius: 10, fontSize: 13, lineHeight: 1.5,
            }}
          >
            <div style={{ color: theme.colors.text.muted, marginBottom: 4 }}>
              You are currently signed in as
            </div>
            <div style={{ fontWeight: 700, marginBottom: 10 }}>
              {existingSessionEmail}
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Link
                data-testid="post-payment-error-goto-dashboard"
                to="/"
                style={{
                  padding: '6px 12px', borderRadius: 8,
                  background: theme.colors.primary, color: '#fff',
                  fontWeight: 700, fontSize: 12, textDecoration: 'none',
                }}
              >
                Go to my dashboard
              </Link>
              <button
                data-testid="post-payment-error-signout"
                onClick={handleSignOutErr}
                style={{
                  padding: '6px 12px',
                  border: `1px solid ${theme.colors.borders}`,
                  borderRadius: 8, background: '#fff',
                  color: theme.colors.text.primary,
                  fontWeight: 700, fontSize: 12, cursor: 'pointer',
                }}
              >
                Sign out and use a different account
              </button>
            </div>
          </div>
        )}
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          {!existingSessionEmail && (
            <Link data-testid="post-payment-error-login" to="/auth" style={{ color: theme.colors.primary, fontWeight: 700 }}>Login</Link>
          )}
          <Link data-testid="post-payment-error-support" to="/support" style={{ color: theme.colors.text.secondary, fontWeight: 700 }}>Contact Support</Link>
        </div>
      </Card>
    );
  }

  const c = state.ctx || {};
  const needsPw = !!c.requires_password_setup;
  const tokenEmail = (c.email || '').toLowerCase();
  // Session mismatch guard: a stale JWT for a *different* user is loaded
  // in this browser. This is the case reported in BUG #8 — clicking "Sign
  // in here" or navigating to /auth would silently route the browser into
  // whatever dashboard that old session belongs to. Force the user to
  // clear the stale session before proceeding.
  const sessionMismatch = Boolean(
    existingSessionEmail && tokenEmail && existingSessionEmail !== tokenEmail
  );

  const handleSignOutAndContinue = () => {
    try {
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
    } catch (_) { /* ignore */ }
    if (typeof setUser === 'function') setUser(null);
    // Reload the same URL so any in-memory state derived from the old
    // session is reset. Token in URL is preserved.
    window.location.reload();
  };

  const inputStyle = {
    width: '100%', padding: '10px 12px', border: `1px solid ${theme.colors.borders}`,
    borderRadius: 10, fontSize: 14, outline: 'none', boxSizing: 'border-box',
  };
  const labelStyle = { display: 'block', fontSize: 13, fontWeight: 700, marginBottom: 6, color: theme.colors.text.primary };

  return (
    <Card>
      <div style={{ fontSize: 12, color: theme.colors.text.muted, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 6 }}>
        Kickback Contract
      </div>
      <h1 data-testid="post-payment-title" style={{ fontSize: 24, fontWeight: 800, margin: 0, marginBottom: 6, color: theme.colors.text.primary }}>
        {needsPw ? 'Activate Your Account' : 'Continue Your Kickback Contract Setup'}
      </h1>

      {sessionMismatch && (
        <div
          data-testid="post-payment-session-mismatch"
          style={{
            display: 'flex', gap: 10, alignItems: 'flex-start',
            padding: 14, marginBottom: 18, marginTop: 6,
            background: '#fef2f2', border: '1px solid #fecaca',
            borderRadius: 10, color: '#991b1b', fontSize: 13, lineHeight: 1.5,
          }}
        >
          <AlertCircle size={16} style={{ flexShrink: 0, marginTop: 2 }} />
          <div style={{ flex: 1 }}>
            <strong>Different account is signed in on this browser.</strong>
            <div style={{ marginTop: 4 }}>
              You are currently signed in as <strong>{existingSessionEmail}</strong>,
              but this activation link is for <strong>{tokenEmail}</strong>. Sign out first
              to avoid mixing up accounts.
            </div>
            <button
              data-testid="post-payment-signout-and-continue"
              onClick={handleSignOutAndContinue}
              style={{
                marginTop: 10, padding: '8px 14px', border: 'none',
                borderRadius: 8, background: '#991b1b', color: '#fff',
                fontWeight: 700, fontSize: 13, cursor: 'pointer',
                display: 'inline-flex', alignItems: 'center', gap: 6,
              }}
            >
              <LogOut size={13} /> Sign out and continue as {tokenEmail}
            </button>
          </div>
        </div>
      )}
      <div style={{ color: theme.colors.text.secondary, marginBottom: 18 }}>
        Welcome{c.user?.name ? `, ${c.user.name}` : ''}. Your <strong>{c.plan_name}</strong> membership
        is <strong>{c.membership_status || 'ready'}</strong>.
      </div>
      <div data-testid="post-payment-summary" style={{ display: 'grid', gap: 6, marginBottom: 22, fontSize: 13 }}>
        {[
          ['Email', c.email],
          ['Role', c.role],
          ['Plan', c.plan_key],
          ['Status', c.membership_status],
        ].map(([k, v]) => (
          <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: `1px solid ${theme.colors.borders}` }}>
            <span style={{ color: theme.colors.text.muted }}>{k}</span>
            <span style={{ fontWeight: 700 }}>{v || '—'}</span>
          </div>
        ))}
      </div>

      {needsPw ? (
        <form onSubmit={handleActivate} data-testid="post-payment-activation-form">
          <div style={{
            display: 'flex', gap: 8, alignItems: 'flex-start',
            padding: 12, marginBottom: 14,
            background: '#eff6ff', border: '1px solid #bfdbfe',
            borderRadius: 10, color: '#1e40af', fontSize: 13, lineHeight: 1.5,
          }}>
            <Lock size={16} style={{ flexShrink: 0, marginTop: 2 }} />
            <div>
              <strong>Set your app password.</strong> This is your Kickback Contract
              app password — separate from your WordPress/PMPro login.
            </div>
          </div>

          <div style={{ marginBottom: 12 }}>
            <label htmlFor="pp-pw1" style={labelStyle}>Create App Password</label>
            <input
              id="pp-pw1"
              data-testid="post-payment-password"
              type="password" autoComplete="new-password"
              value={pw1} onChange={(e) => setPw1(e.target.value)}
              minLength={8} required style={inputStyle}
              placeholder="At least 8 characters"
            />
          </div>
          <div style={{ marginBottom: 16 }}>
            <label htmlFor="pp-pw2" style={labelStyle}>Confirm App Password</label>
            <input
              id="pp-pw2"
              data-testid="post-payment-password-confirm"
              type="password" autoComplete="new-password"
              value={pw2} onChange={(e) => setPw2(e.target.value)}
              minLength={8} required style={inputStyle}
              placeholder="Repeat your password"
            />
          </div>

          {formError && (
            <div
              data-testid="post-payment-form-error"
              style={{
                display: 'flex', gap: 8, alignItems: 'center',
                padding: 10, marginBottom: 12,
                background: '#fef2f2', border: '1px solid #fecaca',
                borderRadius: 10, color: '#991b1b', fontSize: 13,
              }}
            >
              <AlertCircle size={14} /> {formError}
            </div>
          )}

          <button
            data-testid="post-payment-activate"
            type="submit"
            disabled={submitting}
            style={{
              padding: '12px 20px', borderRadius: theme.radius.cta, border: 'none',
              background: theme.colors.primary, color: '#fff', fontWeight: 800, fontSize: 14,
              cursor: submitting ? 'wait' : 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: 8,
            }}
          >
            {submitting ? <Loader2 size={14} /> : <CheckCircle2 size={14} />}
            {submitting ? 'Activating…' : 'Activate & Continue'}
            <ArrowRight size={14} />
          </button>

          <div style={{ marginTop: 12, fontSize: 12, color: theme.colors.text.muted }}>
            Already activated?{' '}
            <Link
              to={`/auth?email=${encodeURIComponent(tokenEmail)}`}
              onClick={sessionMismatch ? (e) => { e.preventDefault(); handleSignOutAndContinue(); } : undefined}
              style={{ color: theme.colors.primary, fontWeight: 700 }}
            >
              Sign in as {tokenEmail}
            </Link>.
          </div>
        </form>
      ) : (
        <button
          data-testid="post-payment-continue"
          disabled={sessionMismatch}
          title={sessionMismatch ? 'Sign out of the other account first.' : ''}
          onClick={() => {
            if (sessionMismatch) return; // guarded — banner handles this
            // If there's no local session at all, go to /auth with the
            // token email as a hint so AuthPage doesn't auto-redirect
            // into some unrelated dashboard.
            if (!existingSessionEmail) {
              navigate(`/auth?email=${encodeURIComponent(tokenEmail)}`);
              return;
            }
            navigate(c.onboarding_route || `/auth?email=${encodeURIComponent(tokenEmail)}`);
          }}
          style={{
            padding: '12px 20px', borderRadius: theme.radius.cta, border: 'none',
            background: sessionMismatch ? '#9ca3af' : theme.colors.primary,
            color: '#fff', fontWeight: 800, fontSize: 14,
            cursor: sessionMismatch ? 'not-allowed' : 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: 8,
          }}
        >
          Continue <ArrowRight size={14} />
        </button>
      )}
    </Card>
  );
}
